import create from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import Bugsnag from '@bugsnag/js'

type FlagValue = boolean | string | number | null

type FlagRequest = {
  id: string
  key: string
  value: FlagValue
}

type FlagRequestsStore = {
  requests: Record<string, Record<string, FlagValue>> // Map of flag keys to instances with unique IDs
  addRequest: (request: FlagRequest) => void
  clearRequest: (request: { id: string; key: string }) => void
}

/**
 * Zustand store to manage feature flag requests.
 * Tracks active flags and their associated instances (by unique IDs).
 *
 * This store works in conjunction with Bugsnag to report feature flag changes:
 * - When a flag becomes active, Bugsnag.addFeatureFlag is called.
 * - When a flag becomes inactive, Bugsnag.clearFeatureFlag is called.
 */
export const useFlagRequestsStore = create(
  subscribeWithSelector<FlagRequestsStore>((set) => ({
    requests: {},
    addRequest: ({ id, key, value }) => {
      if (value !== true) {
        // Ignore flags that are not `true`
        return
      }

      set((state) => {
        const currentInstances = state.requests[key] || {}
        return {
          requests: {
            ...state.requests,
            [key]: { ...currentInstances, [id]: value },
          },
        }
      })
    },
    clearRequest: ({ id, key }) => {
      set((state) => {
        const currentInstances = state.requests[key] || {}
        const { [id]: _, ...remainingInstances } = currentInstances

        const updatedRequests = { ...state.requests, [key]: remainingInstances }

        if (Object.keys(remainingInstances).length === 0) {
          delete updatedRequests[key]
        }

        return { requests: updatedRequests }
      })
    },
  })),
)

// Initialize the listener
let isListenerInitialized = false

/**
 * Listens for changes in the store and ensures Bugsnag is updated in real-time
 * with the current state of feature flags.
 */
export const initializeFlagRequestListener = () => {
  if (isListenerInitialized) return // Prevent multiple listeners
  isListenerInitialized = true

  useFlagRequestsStore.subscribe(
    (state) => state.requests, // Selector: Subscribe to changes in the `requests` state
    (requests, previousRequests) => {
      const changedKeys = new Set([
        ...Object.keys(requests),
        ...Object.keys(previousRequests || {}),
      ])

      changedKeys.forEach((key) => {
        const currentInstances = requests[key] || {}
        const previousInstances = previousRequests?.[key] || {}

        const currentValues = Object.values(currentInstances)
        const previousValues = Object.values(previousInstances)

        const isCurrentlyActive = currentValues.some(
          (value) => value === true, // Only consider `true` as active
        )
        const wasPreviouslyActive = previousValues.some(
          (value) => value === true, // Only consider `true` as active
        )

        if (isCurrentlyActive && !wasPreviouslyActive) {
          // Flag became active
          Bugsnag.addFeatureFlag(key, 'true')
        } else if (!isCurrentlyActive && wasPreviouslyActive) {
          // Flag became inactive
          Bugsnag.clearFeatureFlag(key)
        }
      })
    },
  )
}
